import { motion } from 'framer-motion';

const plans = [
  {
    name: 'Solo',
    tagline: 'Single-provider clinics',
    price: 59.99,
    unit: '/ month',
    audio: '30 hours of audio / month',
    cta: 'Start Free Trial',
    popular: false,
    features: [
      'Voice-to-SOAP note conversion',
      '8 specialties + note templates',
      'OHIP billing codes',
      'Referral letter generation',
      'Follow-up task extraction',
      'Multilingual transcription',
      'Chrome extension + mobile app',
      'Zero-retention by default',
      'Email support — 2 hr response',
    ],
  },
  {
    name: 'Clinic',
    tagline: '2–5 providers',
    price: 49.99,
    unit: '/ user / month',
    audio: '60–150 hours of audio / month',
    cta: 'Start Free Trial',
    popular: true,
    features: [
      'Everything in Solo',
      'Clinic admin dashboard',
      'Shared phrase library',
      'Multi-user billing management',
      'Priority email support — 2 hr response',
      'Onboarding call included',
    ],
  },
  {
    name: 'Large Clinic',
    tagline: '5+ providers',
    price: 39.99,
    unit: '/ user / month',
    audio: '150+ hours of audio / month',
    cta: 'Contact Us',
    popular: false,
    features: [
      'Everything in Clinic',
      'Dedicated account manager',
      'Custom specialty templates',
      'Volume pricing',
      'SLA guarantees',
      'On-site onboarding available',
    ],
  },
];

const comparisonRows = [
  { label: 'Starting price', aurachart: '$60/mo', tali: '~$130/mo', heidi: '~$165/mo' },
  { label: 'PHIPA (Canadian law)', aurachart: '✓', tali: 'Partial', heidi: '✗' },
  { label: 'OHIP billing codes', aurachart: '✓', tali: '✗', heidi: '✗' },
  { label: 'Works with any EMR', aurachart: '✓', tali: 'Limited', heidi: 'Accuro only' },
  { label: 'Canadian data residency', aurachart: '✓', tali: 'Partial', heidi: '✗' },
  { label: 'Mobile app', aurachart: '✓', tali: '✗', heidi: 'Limited' },
  { label: 'Free trial', aurachart: '14 days', tali: '?', heidi: 'Demo only' },
];

export default function Pricing() {
  return (
    <section className="py-20 md:py-28 bg-gray-50" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">Simple. Affordable. Clinician-first.</p>
          <h2 className="mt-3 section-heading">Premium AI at a fraction of the cost</h2>
          <p className="section-sub">
            Most AI scribes charge $100–$600/month per clinician. Aura-Chart starts at $59.99.
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              className={`relative flex flex-col bg-white rounded-2xl p-7 border transition-all ${
                plan.popular
                  ? 'border-primary-400 shadow-lg shadow-primary-100 md:scale-105'
                  : 'border-gray-200 shadow-sm'
              }`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.1 }}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-600 text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">
                    Most popular
                  </span>
                </div>
              )}

              <div>
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <p className="text-sm text-gray-400 mt-0.5">{plan.tagline}</p>
              </div>

              <div className="mt-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-gray-900">${plan.price}</span>
                  <span className="text-sm text-gray-400">{plan.unit}</span>
                </div>
                <p className="mt-1.5 text-xs text-gray-400">{plan.audio}</p>
                <p className="text-xs text-gray-400">+$0.03 per extra minute</p>
              </div>

              <a
                href={
                  plan.name === 'Large Clinic'
                    ? 'mailto:contact@pulsestack.ai'
                    : 'https://chromewebstore.google.com/detail/aura-chart/llkaecoefncjopopkbkhcmajliikeani'
                }
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-6 w-full inline-flex justify-center items-center px-5 py-3 rounded-lg text-sm font-semibold min-h-[48px] transition-colors ${
                  plan.popular
                    ? 'bg-primary-600 text-white hover:bg-primary-500'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
              >
                {plan.cta}
              </a>

              <hr className="my-6 border-gray-100" />

              <ul className="space-y-3">
                {plan.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <svg className="h-4 w-4 text-primary-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Aura-Triage callout */}
        <motion.div
          className="mt-8 rounded-2xl border border-violet-200 bg-violet-50/40 px-7 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <div>
            <p className="text-sm font-semibold text-violet-600">Looking for Aura-Triage pricing?</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Aura-Triage is coming soon for multi-provider clinics. Book a demo to get early access and a quote based on your fax volume and team size.
            </p>
          </div>
          <a
            href="https://docs.google.com/forms/d/1ovweH7b3GY5CO1z4x9vCvklKeddou22MSRNMIakXxz0/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-primary-500 transition-colors whitespace-nowrap"
          >
            Book a Triage Demo →
          </a>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          className="mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="text-center text-xl font-bold text-gray-900 mb-6">
            How Aura-Chart compares
          </h3>
          <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-4 font-semibold text-gray-500 w-1/4">Feature</th>
                  <th className="px-6 py-4 font-semibold text-primary-600 text-center">Aura-Chart</th>
                  <th className="px-6 py-4 font-semibold text-gray-500 text-center">Tali AI</th>
                  <th className="px-6 py-4 font-semibold text-gray-500 text-center">Heidi Health</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={row.label} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    <td className="px-6 py-4 text-gray-600">{row.label}</td>
                    <td className={`px-6 py-4 text-center font-medium ${row.aurachart === '✓' ? 'text-green-600' : row.aurachart === '✗' ? 'text-red-400' : 'text-primary-700'}`}>
                      {row.aurachart}
                    </td>
                    <td className={`px-6 py-4 text-center ${row.tali === '✓' ? 'text-green-600' : row.tali === '✗' ? 'text-red-400' : 'text-gray-500'}`}>
                      {row.tali}
                    </td>
                    <td className={`px-6 py-4 text-center ${row.heidi === '✓' ? 'text-green-600' : row.heidi === '✗' ? 'text-red-400' : 'text-gray-500'}`}>
                      {row.heidi}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
